import { EventObject } from '@xstate/store-react';
import { StepKey } from '../../enums/stepKey';
import { StoreContext } from '../../models/store/context';
import { log } from '@domgen/shared/utils/log';
import { StoreEvent } from '../../enums/storeEvent';
import { AppRoute } from '../../enums/appRoutes';
import { getPreviousStepPath } from '../../utils/router/getPreviousStepPath';
import { findApplianceFromResults } from '@domgen/shared/utils/findApplianceFromResults';

/**
 * @description Back Event Handler
 * This event handler is responsible for handling the "back" event in the application.
 * When this event is triggered, it takes the current step key from the event data,
 * determines the previous step's path using the getPreviousStepPath function, and then navigates the user to that previous step.
 * If a previous step path is found, it uses the enqueue.emit.navigate function to change the route to the previous step.
 * Finally, it returns the unchanged context since this event does not modify any state in the context.
 *
 * UPDATE
 *
 * @param {StoreContext} context - The current store context
 * @param {EventObject & { data: { stepKey: StepKey } }} event - The event object containing the current step key in event.data
 * @param {{ effect: (effect: () => Promise<void>) => void; emit: { navigate: (payload: { path: AppRoute; replace?: boolean }) => void } }} enqueue - An object with an effect function to enqueue side effects and an emit function to navigate to a different route
 * @returns {StoreContext} The unchanged context after handling the back event
 */
export const back = <T extends EventObject = EventObject>(
  context: StoreContext,
  event: T & { data: { stepKey: StepKey } },
  enqueue: {
    effect: (effect: () => Promise<void>) => void;
    emit: { navigate: (payload: { path: AppRoute; replace?: boolean }) => void };
  },
): StoreContext => {
  log.debug({ message: StoreEvent.BACK, data: event.data });

  const isApplianceFromResults = findApplianceFromResults(
    context.form.modelSerial?.modelNumber as string,
    context.applianceLookupResults ?? [],
  );

  if (
    event.data.stepKey == StepKey.PURCHASE_PRICE &&
    context.journeyType === 'test' &&
    isApplianceFromResults
  ) {
    log.debug({
      message:
        'Back event triggered in test journey on purchase price step with appliance in lookup, navigating to landing page',
    });
    enqueue.emit.navigate({ path: AppRoute.LANDING, replace: true });
  } else {
    const previousStep = getPreviousStepPath(event.data.stepKey, context.journeyType);
    if (previousStep) {
      enqueue.emit.navigate({ path: previousStep });
    }
  }
  return context;
};
